import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {version as PWA_VERSION} from '../../package.json'
import useMultiSite from './use-multi-site'
import {useCurrentBasket} from './use-current-basket'
import {useShopperCheckoutTrace} from './useScapiCustomApi'
import {useCurrentCustomer} from './use-current-customer'
import {usePrevious} from '@chakra-ui/react'
import {tryParseJSONObject} from '../utils/utils'

const TRACE_SESSION_EXPIRATION = 60 * 60 * 1000 // 1 hour
const TRACE_KEY = 'traceId'

let pwaSequenceId = 1

const generateNextPwaSequenceId = () => pwaSequenceId++
const generateNextBrowserSequenceId = () => {
    const sequenceId =
        (Number.parseInt(window?.localStorage?.getItem('browserSequenceId'), 10) || 0) + 1
    window?.localStorage?.setItem('browserSequenceId', `${sequenceId}`)
    return sequenceId
}

const clearSystemTraceData = () => {
    window.localStorage?.removeItem(TRACE_KEY)
}

const generateSystemTraceData = (newTraceId) => {
    const timestamp = Date.now()
    const pwaSequenceId = generateNextPwaSequenceId()
    const browserSequenceId = generateNextBrowserSequenceId()
    const storedTraceData = window.localStorage?.getItem(TRACE_KEY)
    const traceData = tryParseJSONObject(storedTraceData) || {
        timestamp,
        sequenceId: 0,
        traceId: newTraceId,
        createdAt: timestamp
    }

    // if the trace data is older than the expiration time, we need to recreate it
    if (
        traceData.timestamp + TRACE_SESSION_EXPIRATION < timestamp &&
        // if we'll recreate the same trace id, no point in destroying the old one
        traceData.traceId !== newTraceId
    ) {
        traceData.traceId = newTraceId
    }

    // update the timestamp and sequence id
    traceData.timestamp = timestamp
    traceData.sequenceId++

    // update the trace data in local storage
    window.localStorage?.setItem(TRACE_KEY, JSON.stringify(traceData))

    return {
        ...traceData,
        pwaVersion: PWA_VERSION,
        pwaSequenceId,
        browserSequenceId
    }
}

/**
 * @typedef {object} TraceData
 * @property {string} category - category level
 * @property {string} [customerEmail] - customer e-mail
 * @property {string} [merchantReference] - Adyen merchant reference
 * @property {number} [basketTotal] - basket total
 * @property {string} [paymentMethod] - type of Adyen payment option
 * @property {object} [additionalContext] - any useful additional data
 */

/**
 * @returns {(function(traceData: TraceData): void)}
 */
export const useCheckoutTracer = () => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {locale} = useMultiSite()
    const {customerType} = useCustomerType()
    const prevCustomerType = usePrevious(customerType)

    const newTraceId = useMemo(
        () =>
            basket?.basketId ||
            // fallback in case we need to trace before there is a basket
            `rnd-${Math.random().toString(36)}`,
        [basket]
    )

    // clear trace data and reset session sequence id on logout
    useEffect(() => {
        if (customerType === 'guest' && prevCustomerType === 'registered') {
            clearSystemTraceData()
        }
    }, [customerType, prevCustomerType])

    const {mutate: trace} = useShopperCheckoutTrace(
        {
            parameters: {
                locale: locale.id
            }
        },
        {}
    )

    // use refs to access the latest values from inside the callback
    const basketRef = useRef(basket)
    basketRef.current = basket
    const customerRef = useRef(customer)
    customerRef.current = customer
    const newTraceIdRef = useRef(newTraceId)
    newTraceIdRef.current = newTraceId

    return useCallback(
        (traceData) => {
            const {traceId, timestamp, sequenceId, ...extraTraceData} = generateSystemTraceData(
                newTraceIdRef.current
            )
            trace({
                body: JSON.stringify({
                    traceId,
                    timestamp,
                    sequenceId,
                    category: traceData?.category,
                    event: traceData?.additionalContext?.context?.method,
                    context: {
                        createdAt: extraTraceData?.createdAt,
                        pwaVersion: extraTraceData?.pwaVersion,
                        pwaSequenceId: extraTraceData?.pwaSequenceId,
                        browserSequenceId: extraTraceData?.browserSequenceId,
                        customerId: customerRef.current?.customerId,
                        customerType: customerRef.current?.customerType,
                        customerNo:
                            basketRef.current?.customerInfo?.customerNo ||
                            customerRef.current?.customerNo,
                        customerEmail:
                            basketRef.current?.customerInfo?.email || customerRef.current?.email,
                        basketId: basketRef.current?.basketId,
                        basket: {
                            numberOfLineItems: basketRef.current?.productItems?.length,
                            shippingMethod: basketRef.current?.shipments?.[0]?.shippingMethod?.name,
                            paymentMethod:
                                basketRef.current?.paymentInstruments?.[0]?.paymentMethodId,
                            total: basketRef.current?.orderTotal || basketRef.current?.productTotal
                        },
                        orderNo: basketRef.current?.orderNo
                    },
                    categoryContext: {
                        api: traceData?.additionalContext?.api,
                        parameters: traceData?.additionalContext?.parameters,
                        response: traceData?.additionalContext?.response
                    }
                })
            })
        },
        [trace]
    )
}
